import { CloseButton, Combobox, FocusTrap, Input, InputWrapper, useCombobox } from '@mantine/core';
import { ReactElement, useState } from 'react';

import { useAutoSelectSoleOption } from '@/hooks/use-auto-select-sole-option.ts';

import { filterPractitionersBySearch, getCalendarIdFromName, getSearchFromCalendarId } from '@/utils/comboboxUtils.ts';

interface Props {
    autoFocusActive: boolean;
    data: PractitionerData[];
    defaultCalendarDocCirrusId?: string;
    label: string;
    setSelectedCalendarId: (value: string | undefined) => void;
    disabled?: boolean;
}

interface PractitionerData {
    id: string;
    name: string;
    calendars: {
        id: string;
        name: string;
    }[];
}

export const CustomCalendarSelect = ({
    data,
    defaultCalendarDocCirrusId,
    label,
    setSelectedCalendarId,
    autoFocusActive = false,
    disabled = false,
}: Props) => {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [search, setSearch] = useState<string>(() => getSearchFromCalendarId(data, defaultCalendarDocCirrusId));
    const [prevData, setPrevData] = useState(data);

    if (data !== prevData) {
        setPrevData(data);
        const freshSearch = getSearchFromCalendarId(data, defaultCalendarDocCirrusId);
        if (freshSearch !== search) {
            if (freshSearch) {
                setSearch(freshSearch);
            } else if (search) {
                setSearch('');
                setTimeout(() => setSelectedCalendarId(undefined), 0);
            }
        }
    }

    useAutoSelectSoleOption({
        options: data.flatMap((practitioner) => practitioner.calendars),
        getKey: (calendar) => calendar.id,
        hasSelection: search !== '',
        onSelect: (calendar) => {
            setSearch(getSearchFromCalendarId(data, calendar.id));
            setSelectedCalendarId(calendar.id);
            combobox.closeDropdown();
        },
        isEnabled: !disabled,
    });

    const filteredGroups = filterPractitionersBySearch(data, search);
    const totalOptions = filteredGroups.length;

    // TODO: memo when logic is fixed
    const groups = (): ReactElement => {
        return (
            <>
                {filteredGroups.map((practitioner) => {
                    if (practitioner.calendars.length > 1) {
                        // Render a grouped Combobox
                        return (
                            <Combobox.Group
                                classNames={{
                                    groupLabel: 'text-sm font-semibold text-black',
                                }}
                                label={practitioner.name}
                                key={practitioner.name}
                            >
                                {practitioner.calendars.map((calendar) => (
                                    <Combobox.Option
                                        classNames={{ option: 'ml-2' }}
                                        value={calendar.name}
                                        key={calendar.id}
                                    >
                                        <div className="text-sm">{calendar.name}</div>
                                    </Combobox.Option>
                                ))}
                            </Combobox.Group>
                        );
                    }

                    // Render ungrouped Combobox.Options
                    return (
                        <Combobox.Option
                            className="text-sm font-semibold text-black"
                            value={practitioner.name}
                            key={practitioner.calendars[0].id}
                        >
                            {practitioner.name}
                        </Combobox.Option>
                    );
                })}
            </>
        );
    };

    return (
        <FocusTrap active={autoFocusActive}>
            <InputWrapper label={label} classNames={{ label: 'font-bold' }}>
                <Combobox
                    store={combobox}
                    withinPortal={false}
                    onOptionSubmit={(val) => {
                        const result = getCalendarIdFromName(data, val);
                        if (result) {
                            const [calendarId] = result;
                            setSelectedCalendarId(calendarId);
                        }
                        setSearch(val);
                        combobox.closeDropdown();
                    }}
                >
                    <Combobox.Target>
                        <Input
                            value={search}
                            onChange={(event) => {
                                combobox.openDropdown();
                                combobox.updateSelectedOptionIndex();
                                setSearch(event.currentTarget.value);
                            }}
                            onClick={() => combobox.openDropdown()}
                            onFocus={() => combobox.openDropdown()}
                            onBlur={() => combobox.closeDropdown()}
                            placeholder="Kalender auswählen"
                            rightSection={
                                search === '' ? (
                                    <Combobox.Chevron />
                                ) : (
                                    <CloseButton
                                        size="sm"
                                        disabled={disabled}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => {
                                            setSelectedCalendarId(undefined);
                                            setSearch('');
                                            combobox.closeDropdown();
                                        }}
                                        aria-label="Clear value"
                                    />
                                )
                            }
                            rightSectionPointerEvents={search === '' || disabled ? 'none' : 'all'}
                            disabled={disabled}
                        />
                    </Combobox.Target>

                    <Combobox.Dropdown className="shadow-sm">
                        <Combobox.Options className="max-h-[200px] overflow-y-auto">
                            {totalOptions > 0 ? groups() : <Combobox.Empty>Nothing found</Combobox.Empty>}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>
            </InputWrapper>
        </FocusTrap>
    );
};
